import React, { useState, useEffect, useRef } from "react";
import practiceInstructorService from "../../services/practiceInstructorService";
import Modal, {
  ModalPrimaryButton,
  ModalSecondaryButton,
} from "../common/Modal";
import { getInitials } from "../../utils/stringUtils";

const getBadgeColor = (type) => {
  const t = (type || "").toString().toUpperCase();
  if (t.includes("INFORMATIK")) return "bg-blue-50 text-blue-700 border border-blue-100";
  if (t.includes("MEDIAMATIK")) return "bg-purple-50 text-purple-700 border border-purple-100";
  if (t.includes("KV")) return "bg-gray-100 text-gray-700 border border-gray-200";
  if (t.includes("DETAIL")) return "bg-orange-50 text-orange-700 border border-orange-100";
  if (t.includes("KUNDENDIALOG")) return "bg-green-50 text-green-700 border border-green-100";
  return "bg-gray-50 text-gray-600 border border-gray-200";
};

const AssignLearnerModal = ({ isOpen, onClose, onAssigned }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmLearner, setConfirmLearner] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!isOpen) {
      setQuery("");
      setResults([]);
      setHasSearched(false);
      setSearchError(null);
      setToast(null);
      setConfirmLearner(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(debounceTimer.current);
    if (query.trim().length < 2) {
      if (query.trim().length === 0) {
        setResults([]);
        setHasSearched(false);
      }
      return;
    }
    debounceTimer.current = setTimeout(() => {
      performSearch(query.trim());
    }, 400);
    return () => clearTimeout(debounceTimer.current);
  }, [query, isOpen]);

  const performSearch = async (q) => {
    setSearching(true);
    setSearchError(null);
    try {
      const data = await practiceInstructorService.searchLearners(q);
      setResults(data);
      setHasSearched(true);
    } catch (err) {
      setSearchError("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleAssignClick = (learner) => {
    if (learner.assignedToMe) return;
    if (learner.currentInstructorId) {
      setConfirmLearner(learner);
    } else {
      doAssign(learner.id);
    }
  };

  const doAssign = async (learnerId) => {
    setConfirmLearner(null);
    setAssigningId(learnerId);
    try {
      await practiceInstructorService.assignToMe(learnerId);
      showToast("success", "Learner successfully assigned to you.");
      onAssigned();
      if (query.trim().length >= 2) {
        await performSearch(query.trim());
      }
    } catch (err) {
      const msg = err.response?.data || "Failed to assign learner. Please try again.";
      showToast("error", msg);
    } finally {
      setAssigningId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Confirm replace modal */}
      {confirmLearner && (
        <Modal
          isOpen
          onClose={() => setConfirmLearner(null)}
          title="Replace current assignment?"
          icon="fa-exclamation-triangle"
          tone="warning"
          size="md"
          zIndex={90}
          footer={
            <>
              <ModalSecondaryButton onClick={() => setConfirmLearner(null)}>
                Cancel
              </ModalSecondaryButton>
              <ModalPrimaryButton
                onClick={() => doAssign(confirmLearner.id)}
                tone="warning"
              >
                <i className="fas fa-check"></i>
                Yes, assign to me
              </ModalPrimaryButton>
            </>
          }
        >
          <p className="text-sm text-slate-cloud leading-relaxed">
            <strong className="text-grey-dusk">
              {confirmLearner.firstName} {confirmLearner.lastName}
            </strong>{" "}
            is currently assigned to{" "}
            <strong className="text-grey-dusk">
              {confirmLearner.currentInstructorName}
            </strong>
            . Assigning them to you ends that assignment.
          </p>
        </Modal>
      )}

      {/* Main modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Assign apprentice"
        subtitle="Search for an apprentice and assign them to yourself"
        icon="fa-user-plus"
        size="xl"
        zIndex={70}
        footer={
          <ModalSecondaryButton onClick={onClose}>Close</ModalSecondaryButton>
        }
      >
          <div>
            {/* Search input */}
            <div className="relative">
              <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search by name, email or username..."
                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none transition-all focus:ring-2 focus:ring-red-100 focus:border-red-400 focus:bg-white"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent"></div>
                </div>
              )}
            </div>
            {query.trim().length > 0 && query.trim().length < 2 && (
              <p className="text-xs text-gray-400 mt-2 ml-1">Type at least 2 characters to search</p>
            )}

          {/* Toast */}
          {toast && (
            <div
              className={`mx-6 mt-4 px-4 py-3 rounded-xl text-sm font-medium flex items-center ${
                toast.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              <i className={`fas ${toast.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"} mr-2`}></i>
              {toast.message}
            </div>
          )}

          {/* Results */}
          <div className="flex-1 overflow-y-auto p-6">
            {searchError && (
              <div className="p-4 bg-red-50 text-red-600 rounded-xl text-sm flex items-center">
                <i className="fas fa-exclamation-circle mr-2"></i>
                {searchError}
              </div>
            )}

            {!hasSearched && !searching && !searchError && (
              <div className="text-center py-12 text-gray-400">
                <i className="fas fa-search text-4xl mb-3 block opacity-30"></i>
                <p className="text-sm">Start typing to search for learners</p>
              </div>
            )}

            {hasSearched && !searching && results.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <i className="fas fa-user-slash text-4xl mb-3 block opacity-30"></i>
                <p className="text-sm font-medium text-gray-500">No learners found</p>
                <p className="text-xs mt-1">Try a different search term</p>
              </div>
            )}

            {results.length > 0 && (
              <div className="space-y-3">
                {results.map((learner) => {
                  const isAssigning = assigningId === learner.id;
                  return (
                    <div
                      key={learner.id}
                      className={`bg-white rounded-xl border p-4 flex items-center justify-between gap-4 transition-all ${
                        learner.assignedToMe
                          ? "border-green-200 bg-green-50/30"
                          : "border-gray-100 hover:border-gray-200 hover:shadow-sm"
                      }`}
                    >
                      {/* Learner info */}
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0">
                          {getInitials(learner.firstName, learner.lastName, learner.username)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-800 text-sm">
                              {learner.firstName} {learner.lastName}
                            </span>
                            {learner.professionType && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getBadgeColor(learner.professionType)}`}>
                                {learner.professionType}
                              </span>
                            )}
                            {learner.currentLehrjahr && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-500 border border-gray-100">
                                {learner.currentLehrjahr}. Lehrjahr
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 truncate mt-0.5">
                            {learner.email}
                            {learner.username && (
                              <span className="ml-2 text-gray-300">· @{learner.username}</span>
                            )}
                          </p>
                          {learner.currentInstructorName && !learner.assignedToMe && (
                            <p className="text-xs text-amber-600 mt-0.5 flex items-center">
                              <i className="fas fa-user-tie mr-1 text-amber-400"></i>
                              Assigned to {learner.currentInstructorName}
                            </p>
                          )}
                          {learner.assignedToMe && (
                            <p className="text-xs text-green-600 mt-0.5 flex items-center">
                              <i className="fas fa-check-circle mr-1"></i>
                              Already assigned to you
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Action */}
                      <div className="shrink-0">
                        {learner.assignedToMe ? (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700">
                            <i className="fas fa-check mr-1.5"></i>
                            Assigned
                          </span>
                        ) : (
                          <button
                            onClick={() => handleAssignClick(learner)}
                            disabled={isAssigning}
                            className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60 disabled:cursor-wait"
                          >
                            {isAssigning ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            ) : (
                              <>
                                <i className="fas fa-user-plus mr-1.5 text-xs"></i>
                                Assign to me
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          </div>
      </Modal>
    </>
  );
};

export default AssignLearnerModal;
